(() => {
    "use strict";

    let pageSignal = null;

    function prefersReducedMotion() {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    function isCoarsePointer() {
        return window.matchMedia("(hover: none), (pointer: coarse)").matches;
    }

    function nextSection(root) {
        if (isCoarsePointer()) {
            const rope = root.querySelector(".hero-sobre-site-rope");
            if (rope) {
                return rope;
            }
        }

        return root.parentElement?.querySelector(":scope > .site-section")
            || document.getElementById("sobre-institucional");
    }

    function scrollToFoldTarget(target) {
        if (!target) {
            return;
        }

        const behavior = prefersReducedMotion() ? "auto" : "smooth";
        const root = document.documentElement;
        root.classList.add("is-fold-scroll-pending");

        target.scrollIntoView({
            behavior,
            block: "start",
        });

        const releaseSnap = () => {
            root.classList.remove("is-fold-scroll-pending");
        };

        if (behavior === "auto") {
            releaseSnap();
            return;
        }

        if ("onscrollend" in window) {
            window.addEventListener("scrollend", releaseSnap, { once: true });
        }

        window.setTimeout(releaseSnap, 900);
    }

    function bindFoldScrollHints(root, signal) {
        const scope = root || document;
        scope.querySelectorAll("[data-sobre-fold-scroll-target]").forEach((trigger) => {
            if (trigger.dataset.foldScrollBound === "true") {
                return;
            }

            trigger.dataset.foldScrollBound = "true";
            trigger.addEventListener("click", (event) => {
                event.preventDefault();
                const selector = trigger.getAttribute("data-sobre-fold-scroll-target") || "";
                if (selector === "") {
                    return;
                }

                scrollToFoldTarget(document.querySelector(selector));
            }, { signal });
        });
    }

    function bindScrollHint(root, signal) {
        const next = nextSection(root);
        if (!next) {
            return;
        }

        root.querySelectorAll("[data-sobre-scroll]").forEach((trigger) => {
            trigger.addEventListener("click", (event) => {
                event.preventDefault();
                next.scrollIntoView({
                    behavior: prefersReducedMotion() ? "auto" : "smooth",
                    block: "start",
                });
            }, { signal });
        });
    }

    function bindPosterFallback(root) {
        const poster = root.querySelector("img[data-poster-fallback]");
        const fallback = poster?.getAttribute("data-poster-fallback") || "";
        if (!poster || fallback === "") {
            return;
        }
        poster.addEventListener("error", () => {
            poster.removeAttribute("data-poster-fallback");
            poster.src = fallback;
        }, { once: true });
    }

    function usesMobileRopeSheet() {
        return isCoarsePointer() || window.matchMedia("(max-width: 900px)").matches;
    }

    function closeRopeInsightSheet(rope) {
        const sheet = rope.querySelector("[data-rope-insight-sheet]");
        const body = rope.querySelector("[data-rope-insight-sheet-body]");
        if (!sheet) {
            return;
        }

        sheet.hidden = true;
        if (body) {
            body.textContent = "";
        }

        rope.classList.remove("is-insight-open");
        document.body.classList.remove("sobre-rope-insight-open");
        rope.querySelectorAll("[data-rope-no]").forEach((item) => {
            setRopeItemState(item, false, { skipSheet: true });
        });
    }

    function openRopeInsightSheet(rope, item) {
        const sheet = rope.querySelector("[data-rope-insight-sheet]");
        const body = rope.querySelector("[data-rope-insight-sheet-body]");
        const insight = item.querySelector(".hero-sobre-site-rope-insight");
        if (!sheet || !body || !insight) {
            return;
        }

        body.textContent = insight.textContent.trim();
        sheet.hidden = false;
        rope.classList.add("is-insight-open");
        document.body.classList.add("sobre-rope-insight-open");
    }

    function setRopeItemState(item, isOpen, options = {}) {
        const rope = item.closest(".hero-sobre-site-rope");
        const insight = item.querySelector(".hero-sobre-site-rope-insight");
        const triggers = item.querySelectorAll(
            ".hero-sobre-site-rope-knot-hit, .hero-sobre-site-rope-word-btn"
        );

        item.classList.toggle("is-active", isOpen);
        triggers.forEach((trigger) => {
            trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
        });

        if (insight) {
            insight.setAttribute("aria-hidden", isOpen ? "false" : "true");
        }

        if (!rope || !usesMobileRopeSheet()) {
            return;
        }

        if (options.skipSheet) {
            return;
        }

        if (isOpen) {
            openRopeInsightSheet(rope, item);
            const items = Array.from(rope.querySelectorAll("[data-rope-no]"));
            const itemIndex = items.indexOf(item);
            if (itemIndex >= 0) {
                document.dispatchEvent(new CustomEvent("sobre:interactive-used", {
                    detail: { id: `hero-rope-${itemIndex + 1}` },
                }));
            }
            return;
        }

        const hasOpen = rope.querySelector("[data-rope-no].is-active") !== null;
        if (!hasOpen) {
            closeRopeInsightSheet(rope);
        }
    }

    function bindRopeInsights(rope, signal) {
        const items = Array.from(rope.querySelectorAll("[data-rope-no]"));
        if (items.length === 0) {
            return;
        }

        const useTouchToggle = usesMobileRopeSheet();
        const sheet = rope.querySelector("[data-rope-insight-sheet]");
        const closeButton = rope.querySelector("[data-rope-insight-close]");

        if (closeButton) {
            closeButton.addEventListener("click", (event) => {
                event.preventDefault();
                event.stopPropagation();
                closeRopeInsightSheet(rope);
            }, { signal });
        }

        items.forEach((item) => {
            const triggers = item.querySelectorAll(
                ".hero-sobre-site-rope-knot-hit, .hero-sobre-site-rope-word-btn"
            );

            triggers.forEach((trigger) => {
                trigger.addEventListener("click", (event) => {
                    if (!useTouchToggle) {
                        return;
                    }

                    event.preventDefault();
                    event.stopPropagation();
                    const isOpen = item.classList.contains("is-active");
                    items.forEach((entry) => setRopeItemState(entry, false, { skipSheet: true }));
                    if (!isOpen) {
                        setRopeItemState(item, true);
                    } else {
                        closeRopeInsightSheet(rope);
                    }
                }, { signal });
            });
        });

        if (!useTouchToggle) {
            return;
        }

        document.addEventListener("pointerdown", (event) => {
            if (!sheet || sheet.hidden) {
                return;
            }

            if (event.target.closest("[data-rope-insight-sheet], [data-rope-no]")) {
                return;
            }

            closeRopeInsightSheet(rope);
        }, { signal });
    }

    function bindVideo(root, signal) {
        if (root.classList.contains("is-playing")) {
            return;
        }

        bindPosterFallback(root);

        const button = root.querySelector("[data-sobre-video-play]");
        const frame = root.querySelector("[data-sobre-video-frame]");
        const embed = root.getAttribute("data-embed") || "";
        if (!button || !frame || embed === "") {
            return;
        }

        button.addEventListener("click", () => {
            document.dispatchEvent(new CustomEvent("sobre:interactive-used", {
                detail: { id: "intro-video" },
            }));

            const separator = embed.includes("?") ? "&" : "?";
            const iframe = document.createElement("iframe");
            iframe.src = `${embed}${separator}autoplay=1`;
            iframe.title = button.getAttribute("data-video-title") || "YouTube";
            iframe.setAttribute(
                "allow",
                "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            );
            iframe.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
            iframe.allowFullscreen = true;
            frame.replaceChildren(iframe);
            root.classList.add("is-playing");
        }, { signal });
    }

    function boot() {
        pageSignal?.abort();
        pageSignal = new AbortController();
        const { signal } = pageSignal;

        document.querySelectorAll(".hero-sobre-site").forEach((root) => {
            bindScrollHint(root, signal);
            bindFoldScrollHints(root, signal);
        });

        bindFoldScrollHints(document, signal);

        document.querySelectorAll("[data-sobre-video]").forEach((root) => {
            bindVideo(root, signal);
        });

        document.querySelectorAll(".hero-sobre-site-rope").forEach((rope) => {
            bindRopeInsights(rope, signal);
        });
    }

    window.siteReady(boot);
})();
